import {
  ArrowClockwiseIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowsClockwiseIcon,
  BellIcon,
  BookOpenIcon,
  BuildingsIcon,
  CalendarBlankIcon,
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CaretUpIcon,
  ChatCircleIcon,
  CheckCircleIcon,
  CheckIcon,
  ClipboardTextIcon,
  ClockIcon,
  CopyIcon,
  DotsThreeIcon,
  DownloadSimpleIcon,
  ExportIcon,
  EyeIcon,
  EyeSlashIcon,
  FolderOpenIcon,
  FolderSimpleIcon,
  FunnelIcon,
  GearIcon,
  HouseIcon,
  ImageIcon,
  InfoIcon,
  ListChecksIcon,
  MagnifyingGlassIcon,
  MinusIcon,
  MoonIcon,
  NotePencilIcon,
  PencilSimpleIcon,
  PlusIcon,
  PushPinIcon,
  PushPinSlashIcon,
  PuzzlePieceIcon,
  RowsIcon,
  SortAscendingIcon,
  SortDescendingIcon,
  SpinnerIcon,
  TrashIcon,
  UserIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import type { ComponentType } from 'react';

import { EmptyInboxIcon } from './custom/empty-inbox-icon';

export type IconWeight = 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';

/**
 * What the registry asks of a glyph, declared here rather than imported from the
 * vendor. Swapping icon families then means a new registry, not new call sites.
 */
export interface RegistryIconProps {
  weight?: IconWeight;
  color?: string;
  className?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
}

export type IconLoader = () => Promise<ComponentType<RegistryIconProps>>;

/**
 * A glyph is either present or fetched. The fetched form is wrapped because
 * `ComponentType` is itself a function type, so `typeof` cannot separate the two
 * at compile time even though it can at runtime — a Phosphor icon is a
 * ForwardRefExoticComponent, which is an object.
 */
export interface LazyIcon {
  load: IconLoader;
}

export type IconEntry = ComponentType<RegistryIconProps> | LazyIcon;

/**
 * A lazy entry must keep its `import()` and its export name in one expression,
 * with the name a literal: a variable key, or the same literal reached through a
 * helper, both stop the bundler resolving the export and pull in the whole
 * 4.9 MB family.
 */
export const iconRegistry = {
  search: MagnifyingGlassIcon,
  filter: FunnelIcon,
  'sort-ascending': SortAscendingIcon,
  'sort-descending': SortDescendingIcon,
  more: DotsThreeIcon,
  edit: PencilSimpleIcon,
  delete: TrashIcon,
  add: PlusIcon,
  export: ExportIcon,
  download: DownloadSimpleIcon,
  copy: CopyIcon,
  refresh: ArrowsClockwiseIcon,
  'arrow-left': ArrowLeftIcon,
  'arrow-right': ArrowRightIcon,
  /* A caret points at a menu; an arrow moves a thing. The column menu's "move left"/"move right"
     mean the second, so they must not borrow the first. */
  'chevron-down': CaretDownIcon,
  'chevron-up': CaretUpIcon,
  'chevron-left': CaretLeftIcon,
  'chevron-right': CaretRightIcon,
  'push-pin': PushPinIcon,
  'push-pin-slash': PushPinSlashIcon,
  eye: EyeIcon,
  'eye-slash': EyeSlashIcon,
  rows: RowsIcon,
  check: CheckIcon,
  /* Eager, like `check`: a status cell renders one per row, and a lazy glyph would
     Suspense-flash down the whole column on first paint. */
  'check-circle': CheckCircleIcon,
  minus: MinusIcon,
  close: XIcon,
  calendar: CalendarBlankIcon,
  contact: UserIcon,
  matter: FolderSimpleIcon,
  dashboard: HouseIcon,
  document: FolderOpenIcon,
  'book-open': BookOpenIcon,
  'note-pencil': NotePencilIcon,
  library: BuildingsIcon,
  docket: ClipboardTextIcon,
  'list-checks': ListChecksIcon,
  clock: ClockIcon,
  chat: ChatCircleIcon,
  puzzle: PuzzlePieceIcon,
  settings: GearIcon,
  notification: BellIcon,
  theme: MoonIcon,
  warning: WarningIcon,
  info: InfoIcon,
  spinner: SpinnerIcon,
  /* Radially symmetric, which is what makes it read under `animate-spin`. */
  'spinner-arrow': ArrowClockwiseIcon,
  image: ImageIcon,
  /* Not a Phosphor glyph — no stock icon draws an inbox tray from this angle. */
  empty: EmptyInboxIcon,

  invoice: { load: () => import('@phosphor-icons/react').then((m) => m.InvoiceIcon) },
  'shield-check': { load: () => import('@phosphor-icons/react').then((m) => m.ShieldCheckIcon) },
  archive: { load: () => import('@phosphor-icons/react').then((m) => m.ArchiveIcon) },
} as const satisfies Record<string, IconEntry>;

/**
 * Exported so a consumer can type its own lookups against it. Annotating the
 * registry `Record<string, …>` instead would collapse this to `string`, and a
 * typo would ship.
 */
export type AppIconName = keyof typeof iconRegistry;
